"use client";

import {
  type CSSProperties,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./FormulaViewer.css";

export type FormulaViewerToken =
  | {
      kind: "reference";
      label: string;
      value: string;
      referenceId: string;
      detail?: string;
      typeLabel?: string;
    }
  | { kind: "operator"; value: string }
  | { kind: "literal"; value: string };

export interface FormulaViewerProps {
  title: string;
  tokens?: FormulaViewerToken[];
  onClose: () => void;
  onOpenReference?: (referenceId: string) => void;
}

export interface FormulaViewerButtonProps {
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/** Compact trigger for a read-only formula viewer. */
export function FormulaViewerButton({
  label,
  onClick,
}: FormulaViewerButtonProps) {
  return (
    <button
      type="button"
      aria-label={`View formula for ${label}`}
      className="formula-viewer-button"
      onClick={onClick}
    >
      ƒx
    </button>
  );
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

type ReferenceTokenProps = {
  token: Extract<FormulaViewerToken, { kind: "reference" }>;
  onShowDetail: (
    token: Extract<FormulaViewerToken, { kind: "reference" }>,
    trigger: HTMLElement,
  ) => void;
  onHideDetail: () => void;
  onOpenReference?: (referenceId: string) => void;
};

function ReferenceToken({
  token,
  onShowDetail,
  onHideDetail,
  onOpenReference,
}: ReferenceTokenProps) {
  const content = (
    <>
      <span>{token.label}</span>
      <span
        className={`formula-viewer-reference-value${token.value === "Needs decision" ? " formula-viewer-reference-value-needs" : ""}`}
      >
        {token.value}
      </span>
    </>
  );

  if (!onOpenReference) {
    return <span className="formula-viewer-reference">{content}</span>;
  }

  return (
    <button
      type="button"
      className="formula-viewer-reference"
      aria-label={`Open ${token.label} in side panel`}
      onMouseEnter={(event) => onShowDetail(token, event.currentTarget)}
      onMouseLeave={onHideDetail}
      onFocus={(event) => onShowDetail(token, event.currentTarget)}
      onBlur={onHideDetail}
      onClick={() => onOpenReference(token.referenceId)}
    >
      {content}
    </button>
  );
}

/** Read-only, focus-trapped formula explainer for modelled line items. */
export function FormulaViewer({
  title,
  tokens = [],
  onClose,
  onOpenReference,
}: FormulaViewerProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const [detail, setDetail] = useState<{
    token: Extract<FormulaViewerToken, { kind: "reference" }>;
    triggerRect: DOMRect;
    style: CSSProperties;
  } | null>(null);
  onCloseRef.current = onClose;

  function showDetail(
    token: Extract<FormulaViewerToken, { kind: "reference" }>,
    trigger: HTMLElement,
  ) {
    setDetail({
      token,
      triggerRect: trigger.getBoundingClientRect(),
      style: {
        position: "fixed",
        left: 12,
        top: 12,
        zIndex: 101,
      },
    });
  }

  useLayoutEffect(() => {
    if (!detail || !detailRef.current) return;

    const { triggerRect } = detail;
    const tooltipRect = detailRef.current.getBoundingClientRect();
    const gutter = 12;
    const gap = 8;
    const left = Math.max(
      gutter,
      Math.min(
        triggerRect.left,
        window.innerWidth - tooltipRect.width - gutter,
      ),
    );
    const preferredTop = triggerRect.top - tooltipRect.height - gap;
    const top = Math.max(
      gutter,
      Math.min(
        preferredTop < gutter ? triggerRect.bottom + gap : preferredTop,
        window.innerHeight - tooltipRect.height - gutter,
      ),
    );

    if (detail.style.left !== left || detail.style.top !== top) {
      setDetail((current) =>
        current ? { ...current, style: { ...current.style, left, top } } : null,
      );
    }
  }, [detail]);

  useEffect(() => {
    closeRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return createPortal(
    <div className="formula-viewer-overlay">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close formula viewer"
        className="formula-viewer-scrim"
        onClick={onClose}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="formula-viewer-dialog"
      >
        <header className="formula-viewer-header">
          <div>
            <div className="formula-viewer-eyebrow">How this is calculated</div>
            <h2 id={titleId} className="formula-viewer-title">
              {title}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close formula viewer"
            className="formula-viewer-close"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="formula-viewer-body">
          <div className="formula-viewer-canvas">
            {tokens.length === 0 ? (
              <span className="formula-viewer-empty">
                No formula set for this line item.
              </span>
            ) : (
              tokens.map((token, index) => {
                if (token.kind === "reference") {
                  return (
                    <ReferenceToken
                      key={`${token.referenceId}-${index}`}
                      token={token}
                      onShowDetail={showDetail}
                      onHideDetail={() => setDetail(null)}
                      onOpenReference={onOpenReference}
                    />
                  );
                }
                return (
                  <span
                    key={`${token.value}-${index}`}
                    className={
                      token.kind === "operator"
                        ? "formula-viewer-operator"
                        : "formula-viewer-literal"
                    }
                  >
                    {token.value}
                  </span>
                );
              })
            )}
          </div>
          <p className="formula-viewer-guidance">
            {onOpenReference
              ? "Hover a value for detail, or click it to open that assumption."
              : "References are shown for context."}
          </p>
        </div>
        <footer className="formula-viewer-footer">
          <button
            type="button"
            className="formula-viewer-done"
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </section>
      {detail && (
        <div
          ref={detailRef}
          className="formula-viewer-detail"
          style={detail.style}
          role="tooltip"
        >
          <span className="formula-viewer-detail-type">
            {detail.token.typeLabel ?? "Assumption"}
          </span>
          <span className="formula-viewer-detail-label">
            {detail.token.label}
          </span>
          {detail.token.detail && (
            <span className="formula-viewer-detail-copy">
              {detail.token.detail}
            </span>
          )}
          <span
            className={`formula-viewer-detail-value${detail.token.value === "Needs decision" ? " formula-viewer-detail-value-needs" : ""}`}
          >
            {detail.token.value}
          </span>
          <span className="formula-viewer-detail-hint">
            → Open in side panel
          </span>
        </div>
      )}
    </div>,
    document.body,
  );
}
